import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu } from 'lucide-react';
import Sidebar from './Sidebar';
import BottomTabs from './BottomTabs';
import NotificationBell from '../NotificationBell';
import FreigabeZaehler from '../FreigabeZaehler';
import NeueNachrichtenPopup from '../NeueNachrichtenPopup';
import Diktat from '../Diktat';
import PiaSchwebeknopf from '../PiaSchwebeknopf';
import { UmsatzProvider } from '../UmsatzSperre';

export default function AppLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const ort = useLocation();
  const flaeche = useRef(null);
  // Die Neugestaltung gilt nur hier, im angemeldeten CRM: öffentliche Seiten bekommen die Klasse nie (index.css)
  useLayoutEffect(() => {
    document.documentElement.classList.add('crm-neu');
    return () => document.documentElement.classList.remove('crm-neu');
  }, []);
  // Seitenwechsel gleitet ein (Neugestaltung 12.10.2026) — ohne die Seite neu aufzubauen, und nicht bei „Bewegung reduzieren"
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    flaeche.current?.animate?.([{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
      { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
  }, [ort.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      {/* Mobile Header */}
      <header data-print="rahmen" className="fixed top-0 left-0 right-0 z-30 h-14 border-b border-border bg-background/95 backdrop-blur-md flex items-center px-4 gap-3 md:hidden">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" className="text-foreground p-1">
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo-black.png" alt="Performance Center" className="h-7 object-contain" />
        </div>
        <div className="ml-auto flex items-center gap-2">
          <FreigabeZaehler />
          <NotificationBell />
          {user && (
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-semibold text-primary">
              {user.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
        </div>
      </header>

      {/* Desktop Notification Bar */}
      <div data-print="rahmen" className={`hidden md:flex fixed top-0 right-0 z-30 h-12 items-center px-5 gap-3 transition-all duration-300 ${collapsed ? 'left-[68px]' : 'left-[240px]'}`}>
        <div className="ml-auto flex items-center gap-3">
          <FreigabeZaehler />
          <NotificationBell />
          {user && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                {user.name?.charAt(0)?.toUpperCase()}
              </div>
              <span className="text-xs font-medium text-muted-foreground hidden lg:block">{user.name}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main
        className={`transition-all duration-300 min-h-screen pt-14 pb-16 md:pt-12 md:pb-0 ${
          collapsed ? 'md:ml-[68px]' : 'md:ml-[240px]'
        }`}
      >
        <div ref={flaeche} className="p-4 md:p-6 max-w-[1600px] mx-auto">
          {/* Umsatz-Sperre (30.10.2026): baut den Inhalt nach Entsperren/Sperren neu auf */}
          <UmsatzProvider>{children}</UmsatzProvider>
        </div>
      </main>

      <NeueNachrichtenPopup />
      {/* Diktieren in jedes Textfeld (30.10.2026): nur hier, im angemeldeten CRM */}
      <Diktat />
      {/* Pia überall (30.10.2026): schwebender Knopf + Seitenfenster, nur für den Inhaber (⌘J / Strg+J) */}
      <PiaSchwebeknopf />

      {/* Bottom Tabs — mobile only */}
      <BottomTabs onMoreClick={() => setMobileOpen(true)} />
    </div>
  );
}
